/**
 * schemas/types.ts — typed errors raised by the service-boundary Zod
 * parses defined in this package.
 *
 * Why a separate module: the error classes are referenced from BOTH
 * the service layer (where they're thrown) and the UI layer (where
 * RetryBanner branches on them) — keeping them in `schemas/` would
 * pull the entire Zod schema graph into any view that wants to catch
 * a shape error. This module has zero schema imports so callers pay
 * only the cost of the class.
 *
 * SSOT discipline:
 *   - `ResponseShapeError` is the ONLY error a caller catches when a
 *     backend response fails its boundary parse. The raw `ZodError`
 *     stays inside the service module (forwarded to `captureError`
 *     as `extra.issues`) so we don't leak Zod's surface into views.
 *   - `ZodValidationError` is an alias used at the `captureError`
 *     forwarding site so the SSOT call reads "this is a Zod validation
 *     failure" without the importer pulling Zod's `ZodError` symbol
 *     directly. It is structurally a `ZodError` re-export.
 *
 * Why not bare `Error` with a message: useAsyncResource normalises any
 * `unknown` reject into an `Error` already; a custom class lets the
 * UI layer differentiate "shape error from the boundary parse"
 * (RetryBanner can show "Server returned unexpected data; the team
 * has been notified") from a generic transport failure ("Could not
 * load this section. Try again.") without string-sniffing.
 */

import { ZodError } from 'zod';

/** Re-export so `captureError` calls can reference a stable name
 *  without importing Zod directly. Keeps the captureError site readable:
 *  `captureError(new ZodValidationError(...), ...)`. */
export { ZodError as ZodValidationError };

/**
 * Thrown from `mentorshipService.ts` when a response payload fails
 * its boundary Zod parse. The thrown error carries:
 *
 *   - `op` — the originating service function name (e.g. "listReports").
 *     RetryBanner can branch on this for endpoint-specific copy if it
 *     wants; default copy is the same across all shape errors.
 *   - `issues` — the underlying Zod issue list. Not rendered in the UI
 *     (it's PII-shaped backend-internal data) — kept so a future devtool
 *     panel or e2e harness can introspect.
 *
 * The thrown class identity is the load-bearing part: views catch it
 * via `instanceof ResponseShapeError` (or by the `name` field for
 * cross-realm tests). Do NOT rely on `.message` for branching — the
 * message text is for human readers only and will move with copy.
 */
export class ResponseShapeError extends Error {
    /** The operation that produced the malformed response (e.g.
     *  "listReports", "listDisputes"). Forwarded as `name` discriminant
     *  to telemetry so dashboards can group by endpoint. */
    public readonly op: string;
    /** Zod's issue list — not rendered to users; surfaced for telemetry
     *  and dev tooling. */
    public readonly issues: ZodError['issues'];

    constructor(op: string, zodError: ZodError) {
        super(
            `Response shape from "${op}" did not match the contract (` +
            `${zodError.issues.length} issue${zodError.issues.length === 1 ? '' : 's'}).`,
        );
        // `name` is set so `instanceof` checks remain robust across
        // realms (tests sometimes pull the class through a fresh module
        // graph and `instanceof` falls back to a name comparison).
        this.name = 'ResponseShapeError';
        this.op = op;
        this.issues = zodError.issues;
    }
}
